import React, { useCallback, useEffect } from 'react'
import { User } from 'types'
import { Crisp } from 'crisp-sdk-web'

const ChatContext = /*#__PURE__*/ React.createContext<{
  setUser: (user: User) => void
  loadChat: () => void
  closeChat: () => void
  showChat: () => void
}>({
  setUser: () => undefined,
  loadChat: () => undefined,
  closeChat: () => undefined,
  showChat: () => undefined,
})

export const ChatProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const setUser = useCallback((user: User) => {
    try {
      Crisp.user.setEmail(user.email)
      Crisp.user.setNickname(`${user.firstName} ${user.middleName} ${user.lastName}`)
    } catch (e) {
      console.error(e)
    }
  }, [])

  const loadChat = useCallback(() => {
    if (!process.env.REACT_APP_WEBSITE_ID) return

    Crisp.configure(process.env.REACT_APP_WEBSITE_ID, {
      autoload: false,
    })
    Crisp.load()
  }, [])

  const closeChat = useCallback(() => {
    Crisp.chat.hide()
  }, [])

  const showChat = useCallback(() => {
    Crisp.chat.show()
  }, [])

  return (
    <ChatContext.Provider value={{ setUser, loadChat, closeChat, showChat }}>
      {children}
    </ChatContext.Provider>
  )
}

export const useSetUser = () => {
  const context = React.useContext(ChatContext)
  return (user?: User) => {
    if (user) {
      context.setUser(user)
    }
    context.loadChat()
    context.showChat()
  }
}

export const useLoadChat = () => {
  const context = React.useContext(ChatContext)
  return context.loadChat
}

export const useCloseChat = () => {
  const context = React.useContext(ChatContext)
  return context.closeChat
}

export const useShowChat = () => {
  const context = React.useContext(ChatContext)
  return context.showChat
}
